/**
 * WorkshopOrchestrator - facilitator command center for one workshop.
 *
 * Shows the instance's module chain (#682/#683): one scroll-snap section per
 * module, in chain order, with what must be entered by hand, its screens and
 * timing, and the module's own facilitator panel. The chain is visible before
 * the workshop starts, so modules can be prepared; Käynnistä runs it.
 *
 * The workshop lock and participant PIN live in the run strip of the stage
 * row (AdminHeader/RunStrip); planned screen content on its own page,
 * /admin/screen-content (specs/admin-instance-lifecycle.md).
 * The instance lock (settings.locked) — see specs/settings-map.md.
 *
 * Route: /admin/workshop?instance=<instanceId>
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Play } from 'lucide-react';
import API_ENDPOINTS from '../config/api';
import AdminHeader from '../components/AdminHeader/AdminHeader';
import PhaseNav from '../components/Workshop/PhaseNav';
import PhaseSection from '../components/Workshop/PhaseSection';
import ModuleAdminContent from '../components/Workshop/ModuleAdminContent';
import toPresentation from '../components/Workshop/toPresentation';
import PreworkCurationAdmin from '../components/Workshop/prework/PreworkCurationAdmin';
import useAdminInstance from '../hooks/useAdminInstance';
import useInstanceRecord from '../hooks/useInstanceRecord';
import useWorkshopChain from '../hooks/useWorkshopChain';
import useOrchestratorFlow from '../hooks/useOrchestratorFlow';
import useModuleFlowActions from '../hooks/useModuleFlowActions';
import { LeaveCheckDialog } from '../components/Workshop/ModuleFlowActions';
import GateBadge from '../components/Workshop/GateBadge';
import { useTranslation } from '../hooks/useTranslation.jsx';
import {
  Page, Body, MainScroll, CenterState, Spinner, SelectFlowButton,
  ReadinessBar, ReadinessLink, NoInstanceState, NoInstanceContent,
} from './WorkshopOrchestrator.styles';

function label(obj, language) {
  return obj?.labels?.[language] || obj?.labels?.fi || obj?.id || '';
}

export default function WorkshopOrchestrator() {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { instanceId } = useAdminInstance();

  const [instanceName, setInstanceName] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  // The instance asks a Kipupiste pre-task: its curation panel belongs to ongelmat.v1, else sits above the modules.
  const [hasKipupiste, setHasKipupiste] = useState(false);
  const [presentation, setPresentation] = useState(null);
  const [instanceLoading, setInstanceLoading] = useState(true);

  const [activePhaseIndex, setActivePhaseIndex] = useState(0);
  const [startError, setStartError] = useState(null);

  // Workshop lock: instance.settings.locked (participant screens respect it too),
  // switched from the run strip; shared through useInstanceRecord.
  const { instance: instanceRecord } = useInstanceRecord(instanceId);
  const isLocked = Boolean(instanceRecord?.settings?.locked);

  const scrollRef = useRef(null);
  const sectionRefs = useRef([]);

  const { chain, lookups, error: chainError, loading: chainLoading, reload: reloadChain, patchStep } = useWorkshopChain(instanceId);
  const { flowState, timer, loadFlowState, startWorkshop, handleFlowAction, adjustTimer } = useOrchestratorFlow(instanceId, { onChainChanged: reloadChain });

  // ─── Instance, scenarios, deck ─────────────────────────────────────

  useEffect(() => {
    if (!instanceId) { setInstanceLoading(false); return; }
    let cancelled = false;
    (async () => {
      setInstanceLoading(true);
      try {
        const [instanceRes, scenariosRes] = await Promise.all([
          fetch(API_ENDPOINTS.INSTANCE(instanceId)),
          fetch(API_ENDPOINTS.INSTANCE_SCENARIOS(instanceId)),
          loadFlowState(),
        ]);
        const instance = await instanceRes.json();
        if (cancelled) return;
        setInstanceName(instance.title || instance.name);
        setHasKipupiste((instance.settings?.preWork || []).some((task) => task?.kind === 'kipupiste'));
        const scenariosData = await scenariosRes.json();
        setScenarios((scenariosData.scenarios || scenariosData || []).filter((s) => s.is_active !== false));
        if (instance.google_presentation_id) {
          fetch(API_ENDPOINTS.PRESENTATION(instance.google_presentation_id))
            .then((res) => (res.ok ? res.json() : null))
            .then((pres) => { if (!cancelled) setPresentation(pres ? toPresentation(pres) : null); })
            .catch(() => setPresentation(null));
        } else {
          setPresentation(null);
        }
      } catch (error) {
        console.error('Failed to load orchestrator data:', error);
      } finally {
        if (!cancelled) setInstanceLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [instanceId, loadFlowState]);

  // ─── The chain's steps (raw rows add the screen_config for the section header) ──

  const steps = useMemo(() => {
    const rawById = Object.fromEntries((flowState?.allSteps || []).map((s) => [s.id, s]));
    return (chain?.steps || []).map((s) => ({
      ...rawById[s.stepId],
      ...s,
      id: s.stepId,
      name: s.name,
      suggested_duration: s.suggestedDuration,
    }));
  }, [chain, flowState?.allSteps]);

  const currentStepId = flowState?.currentStep?.id;
  const currentStepIndex = steps.findIndex((s) => s.id === currentStepId);

  // ─── Scroll-spy ─────────────────────────────────────────────────────

  const activePhaseRef = useRef(0);
  useEffect(() => {
    const container = scrollRef.current;
    if (!container || steps.length === 0) return undefined;
    let rafId = null;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const threshold = container.getBoundingClientRect().top + container.getBoundingClientRect().height * 0.35;
        let best = -1;
        sectionRefs.current.forEach((el, i) => { if (el && el.getBoundingClientRect().top <= threshold) best = i; });
        if (best !== activePhaseRef.current) {
          activePhaseRef.current = best;
          setActivePhaseIndex(best);
        }
      });
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      container.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [steps.length]);

  const handlePhaseClick = useCallback((index) => {
    activePhaseRef.current = index;
    setActivePhaseIndex(index);
    sectionRefs.current[index]?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Follow the live step: the page opens at it, and scrolls to it whenever it
  // moves (Seuraava / Edellinen, or another device). Keyed on the step id, so
  // flow-state polls and the facilitator's own scrolling are left alone.
  const followedStepRef = useRef(null);
  useEffect(() => {
    if (instanceLoading || chainLoading || currentStepIndex < 0 || followedStepRef.current === currentStepId) return;
    const opening = followedStepRef.current === null;
    followedStepRef.current = currentStepId;
    activePhaseRef.current = currentStepIndex;
    setActivePhaseIndex(currentStepIndex);
    sectionRefs.current[currentStepIndex]?.scrollIntoView({ behavior: opening ? 'auto' : 'smooth' });
  }, [instanceLoading, chainLoading, currentStepId, currentStepIndex]);

  const handleStart = useCallback(async () => {
    setStartError(null);
    try {
      await startWorkshop();
    } catch (err) {
      setStartError(err.message);
    }
  }, [startWorkshop]);

  // ─── The live module's gate in the flow bar, and its checks before Seuraava leaves it ──
  // Its buttons are in its section (ModuleActionsShelf), not here (#1217).

  const moduleFlow = useModuleFlowActions(instanceId, currentStepId, { withChecks: true });
  const [unmetChecks, setUnmetChecks] = useState(null);
  const pendingAction = useRef(null);

  const guardedFlowAction = useCallback(async (action) => {
    if (action === 'next') {
      const unmet = await moduleFlow.checkLeave();
      if (unmet.length) {
        pendingAction.current = action;
        setUnmetChecks(unmet);
        return undefined;
      }
    }
    return handleFlowAction(action);
  }, [moduleFlow, handleFlowAction]);

  // ─── Render ────────────────────────────────────────────────────────

  if (!instanceId) {
    return (
      <NoInstanceState>
        <AdminHeader instanceId={null} instanceName={null} />
        <NoInstanceContent>
          <div>
            <h2>{t('workshopOrchestrator.selectInstanceTitle')}</h2>
            <p>{t('workshopOrchestrator.selectInstanceBody')}</p>
            <SelectFlowButton onClick={() => navigate('/admin')}>{t('workshopOrchestrator.adminHome')}</SelectFlowButton>
          </div>
        </NoInstanceContent>
      </NoInstanceState>
    );
  }

  if (instanceLoading || chainLoading) {
    return (
      <Page>
        <AdminHeader instanceId={instanceId} instanceName={instanceName || t('workshopOrchestrator.loading')} />
        <CenterState><Spinner /></CenterState>
      </Page>
    );
  }

  if (!chain) {
    return (
      <Page>
        <AdminHeader instanceId={instanceId} instanceName={instanceName} />
        <CenterState>
          <h2>{t('workshopOrchestrator.chainMissing')}</h2>
          {chainError && <p>{t('workshopOrchestrator.chainLoadFailed', { error: chainError })}</p>}
        </CenterState>
      </Page>
    );
  }

  const handoffNames = (ids) => ids.map((h) => label(lookups.handoffs[h], language)).join(', ');
  const manualNeeded = chain.issues?.manualNeeded || [];
  const orderWarnings = chain.issues?.orderWarnings || [];
  // One readiness line, only while there is something to say.
  const needsAttention = !flowState || manualNeeded.length > 0 || orderWarnings.length > 0;

  return (
    <Page>
      <AdminHeader instanceId={instanceId} instanceName={instanceName} />
      {needsAttention && (
        <ReadinessBar $ok={manualNeeded.length === 0 && orderWarnings.length === 0}>
          {!flowState && (
            <>
              <strong>{t('workshopOrchestrator.notStartedTitle')}.</strong> {t('workshopOrchestrator.notStartedBody')}
              {' '}
              <SelectFlowButton type="button" onClick={handleStart} style={{ display: 'inline-flex', marginLeft: 8 }}>
                <Play size={16} /> {t('workshopOrchestrator.start')}
              </SelectFlowButton>
              {startError && <div style={{ color: '#f87171', marginTop: 6 }}>{t('workshopOrchestrator.startFailed', { error: startError })}</div>}
            </>
          )}
          {manualNeeded.length > 0 && <div>{t('workshopOrchestrator.manualNeededBar', { items: handoffNames(manualNeeded) })}</div>}
          {orderWarnings.length > 0 && <div>{t('workshopOrchestrator.orderWarningBar', { items: handoffNames(orderWarnings) })}</div>}
          <ReadinessLink as={Link} to={`/admin/flow?instance=${instanceId}`}>
            {t('flowBuilder.openBuilder')}
          </ReadinessLink>
        </ReadinessBar>
      )}

      <Body>
        <PhaseNav
          steps={steps}
          currentStepId={currentStepId}
          activePhaseIndex={activePhaseIndex}
          flowState={flowState}
          timer={timer}
          onPhaseClick={handlePhaseClick}
          onFlowAction={guardedFlowAction}
          onTimerAdjust={adjustTimer}
          onStart={handleStart}
          showMobileFlowBar
          gate={<GateBadge checks={moduleFlow.checks} onOpen={() => handlePhaseClick(currentStepIndex)} />}
        />
        <MainScroll ref={scrollRef} $flowPad>
          {/* Pre-work curation is ongelmat.v1's own panel; above the phases only when that module is not in the chain */}
          {hasKipupiste && !steps.some((s) => s.moduleId === 'ongelmat.v1') && (
            <PreworkCurationAdmin instanceId={instanceId} scenarios={scenarios} locked={isLocked} />
          )}
          {steps.map((step, idx) => (
            <PhaseSection
              key={step.id}
              ref={(el) => { sectionRefs.current[idx] = el; }}
              step={step}
              stepIndex={idx}
              isActive={step.id === currentStepId}
              isPast={currentStepIndex >= 0 && idx < currentStepIndex}
            >
              <ModuleAdminContent
                step={step}
                lookups={lookups}
                instanceId={instanceId}
                locked={isLocked}
                scenarios={scenarios}
                hasDeck={Boolean(presentation?.id)}
                onSave={patchStep}
                onChanged={reloadChain}
                onEditScreens={() => navigate(`/admin/screen-content?instance=${instanceId}&step=${step.id}`)}
              />
            </PhaseSection>
          ))}
        </MainScroll>
      </Body>
      {unmetChecks && (
        <LeaveCheckDialog
          checks={unmetChecks}
          actions={moduleFlow.actions}
          onCancel={() => setUnmetChecks(null)}
          onContinue={() => {
            setUnmetChecks(null);
            handleFlowAction(pendingAction.current);
          }}
          onRunAction={async (actionId) => {
            setUnmetChecks(null);
            await moduleFlow.run(actionId);
          }}
        />
      )}
    </Page>
  );
}
